import { Box, Image } from '@chakra-ui/react';
import { HANDLE } from 'constants/bounding-box';
import { OnResizeEvent, ResizingState } from 'modules/Editor/components/BoundingBox';
import { setHandleStyle, unsetHandleStyle } from 'modules/Editor/components/BoundingBox/BoundingBox.helpers';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { LineDirection, OnDrag, OnDragEnd, OnDragStart, OnResizeEnd, OnResizeStart } from 'react-moveable';
import { parseStrictNumber } from 'utils/number';

import { cropToAbsolutePosition } from '../ImageWidget.helpers';
import { MOVEABLE_DRAGGABLE_AREA_CLASS } from './Crop.config';
import { getCrop, getDimensionRestriction, getImageDistanceToCanvas, getOffset } from './Crop.helpers';
import { CropAreaBounds, DraggableImageProps } from './Crop.types';
import { StyledMoveableDraggableImage } from './MoveableDraggableImage.style';

export const DraggableImage = ({
  imageData,
  cropRef,
  areaTranslate,
  setCropAreaBounds,
  imageDragBounds,
  imageMoveableRef,
  zoom,
  dragTargetRef,
  cropImageRef,
  setIsDragging,
  isDragging,
  widgetBaseRef,
  imageStyle,
  onResizeCropImageEnd,
}: DraggableImageProps) => {
  // Keep track whether actual resize dragging started or not and to which direction
  // Used for adding handle styling - hover is not enough if the cursor is moved outside of the handle while dragging resize handle
  const resizeStateRef = useRef<ResizingState>({ actualResizingStarted: false, direction: '' });

  const { url, crop, imageWidth, imageHeight, altText } = imageData;
  const [translate, setTranslate] = useState<number[]>([0, 0]);
  const [top, right, bottom, left] = cropToAbsolutePosition(crop);
  const startPos = useRef({ top: 0, left: 0 });

  const onDragStart = (e: OnDragStart) => {
    setIsDragging(true);

    const target = e.target;
    startPos.current.top = parseStrictNumber(target.style.top);
    startPos.current.left = parseStrictNumber(target.style.left);

    e.set(translate);
  };

  const onDrag = (e: OnDrag) => {
    if (!imageDragBounds) return;
    if (!cropRef.current) return;

    const { left, top, target, dist } = e;
    const [widthDist, topDist] = dist;
    const isDragDown = topDist > 0;
    const isDragRight = widthDist > 0;

    // Dragging the image is limited to the inner bounds of the cropped area
    // -> image cannot be dragged outside of the crop area

    if (isDragDown) {
      const isWithinTopBound = top < imageDragBounds.top;
      if (isWithinTopBound) target.style.top = top + 'px';
      else target.style.top = imageDragBounds.top + 'px';
    } else {
      const isWithinBottomBound = Math.abs(topDist) < cropRef.current.crop.bottom;
      if (isWithinBottomBound) {
        target.style.top = top + 'px';
      } else {
        const bottomLimit = startPos.current.top - cropRef.current.crop.bottom;
        target.style.top = bottomLimit + 'px';
      }
    }

    if (isDragRight) {
      const isWithinLeftBound = left < imageDragBounds.left;
      if (isWithinLeftBound) target.style.left = left + 'px';
      else target.style.left = imageDragBounds.left + 'px';
    } else {
      const isWithinRightBound = Math.abs(widthDist) < cropRef.current.crop.right;
      if (isWithinRightBound) {
        target.style.left = left + 'px';
      } else {
        const rightLimit = startPos.current.left - cropRef.current.crop.right;
        target.style.left = rightLimit + 'px';
      }
    }
  };

  const onDragEnd = (e: OnDragEnd) => {
    setIsDragging(false);

    if (e.lastEvent) setTranslate(e.lastEvent.beforeTranslate);
    if (!cropImageRef.current) return;
    if (!cropRef.current) return;

    const targetEl = e.target as HTMLElement;
    const newCrop = getCrop(cropImageRef.current, targetEl, areaTranslate);
    const newPosition = getImageDistanceToCanvas(targetEl, newCrop.top, newCrop.left);

    cropRef.current.crop = newCrop;
    cropRef.current.position = newPosition;

    const bounds = getBounds(targetEl, widgetBaseRef);
    setCropAreaBounds(bounds as CropAreaBounds);
  };

  const onResizeStart = (e: OnResizeStart) => {
    if (!cropRef.current) return;

    if (!e.target.style.width) {
      e.target.style.width = `${imageWidth}px`;
    }

    if (!e.target.style.height) {
      e.target.style.height = `${imageHeight}px`;
    }

    try {
      const width = parseStrictNumber(e.target.style.width);
      const height = parseStrictNumber(e.target.style.height);

      // Set minimum dimensions for resizing to prevent shrinking past crop area
      const dimensionRestriction = getDimensionRestriction({ width, height }, cropRef.current.crop, e.direction);
      e.setMin([dimensionRestriction.minWidth, dimensionRestriction.minHeight]);
    } catch (error) {
      console.error(error);
    }
  };

  const onResize = (e: OnResizeEvent) => {
    if (!imageDragBounds) return;
    if (!cropRef.current) return;
    if (!dragTargetRef?.current) return;

    // If actual dragging is not started yet, or, this one is first drag (e.isFirstDrag is not behaving as expected)
    setHandleStyle(resizeStateRef, e.direction, `.${MOVEABLE_DRAGGABLE_AREA_CLASS}`);

    dragTargetRef.current.style.top = `${e.drag.top}px`;
    dragTargetRef.current.style.left = `${e.drag.left}px`;
    dragTargetRef.current.style.width = `${e.width}px`;
    dragTargetRef.current.style.height = `${e.height}px`;
  };

  const onResizeEnd = (e: OnResizeEnd) => {
    setIsDragging(false);
    if (!e.lastEvent?.drag) return;
    if (!cropImageRef.current) return;
    if (!cropRef.current) return;
    if (!dragTargetRef.current) return;
    if (e.lastEvent?.drag) setTranslate(e.lastEvent?.drag?.beforeTranslate);
    if (!onResizeCropImageEnd) return;

    const width = parseStrictNumber(e.target.style.width);
    const height = parseStrictNumber(e.target.style.height);

    const targetEl = e.target as HTMLElement;
    const newCrop = getCrop(cropImageRef.current, targetEl, areaTranslate);
    const newPosition = getImageDistanceToCanvas(targetEl, newCrop.top, newCrop.left);

    cropRef.current.crop = newCrop;
    cropRef.current.position = newPosition;

    onResizeCropImageEnd({
      widthPx: width,
      heightPx: height,
    });

    const bounds = getBounds(targetEl, widgetBaseRef);
    setCropAreaBounds(bounds as CropAreaBounds);

    unsetHandleStyle(resizeStateRef, `.${MOVEABLE_DRAGGABLE_AREA_CLASS}`);
  };

  const dragTargetStyle = { top, right, bottom, left, position: 'absolute', zIndex: 1 } as React.CSSProperties;
  const moveableZoom = zoom < 1 ? zoom + 1 : 1;

  return (
    <>
      <Box ref={dragTargetRef} w={`${imageWidth}px`} h={`${imageHeight}px`} style={dragTargetStyle}>
        <Image
          w='100%'
          h='100%'
          userSelect='none'
          cursor='move'
          draggable={false}
          src={url}
          alt={altText}
          style={imageStyle}
        />
      </Box>

      <StyledMoveableDraggableImage
        // see https://github.com/daybrush/moveable/tree/master/packages/react-moveable#react-18-concurrent-mode
        flushSync={flushSync}
        ref={imageMoveableRef}
        target={dragTargetRef.current}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onResizeStart={onResizeStart}
        onResize={onResize}
        onResizeEnd={onResizeEnd}
        zoom={moveableZoom}
        origin={false}
        draggable={true}
        renderDirections={isDragging ? [] : HANDLE.CORNERS}
        resizable={{
          keepRatio: true,
          edge: HANDLE.CORNERS as unknown as LineDirection[],
        }}
        className={MOVEABLE_DRAGGABLE_AREA_CLASS}
      />
    </>
  );
};

/**
 * distance from the draggable image to the widget original position
 * Returns bounds { left, top, right, bottom } as px number for moveable
 */
const getBounds = (dragTargetEl: HTMLElement, widgetBaseEl: HTMLElement) => {
  const dragTargetElOffset = getOffset(dragTargetEl);
  const widgetBaseElOffset = getOffset(widgetBaseEl);

  const top = dragTargetElOffset.top - widgetBaseElOffset.top;
  const left = dragTargetElOffset.left - widgetBaseElOffset.left;
  const right = -(dragTargetElOffset.right - widgetBaseElOffset.right);
  const bottom = -(dragTargetElOffset.bottom - widgetBaseElOffset.bottom);

  return { left, top, right, bottom, position: 'css' };
};
